import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { DashboardService } from '../shared/dashboardservice';
import { AdminService, LogEntry, LogsResponse } from './admin.service';

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminComponent implements OnInit {
    // For Update Claim tab
    referenceNumber: string = '';
    newClaimNumber: string = '';
    updateClaimMessage: string = '';
    totalTransmitCount: number = 0;
    weeklyTransmitCount: number = 0;
    claimIdsTransmittedToday: string[] = [];
    claimIdsTransmittedThisWeek: string[] = [];

    updateClaim() {
      if (!this.referenceNumber || !this.newClaimNumber) {
        this.updateClaimMessage = 'Reference number and new claim number are required.';
        this.changeDetector.detectChanges();
        return;
      }
      this.adminService.updateClaim(this.referenceNumber, this.newClaimNumber).subscribe({
        next: () => {
          this.ngZone.run(() => {
            this.updateClaimMessage = `Claim number and status updated successfully for reference #${this.referenceNumber}.`;
            this.changeDetector.detectChanges();
          });
        },
        error: (err) => {
          this.ngZone.run(() => {
            this.updateClaimMessage = 'Failed to update claim number.';
            this.changeDetector.detectChanges();
          });
        }
      });
    }
  usageMessageFilter: string = '';
  errorMessageFilter: string = '';
  columnDefs = [
    { headerName: 'Timestamp', field: 'timestamp', sortable: true, filter: true },
    { headerName: 'Product', field: 'product', sortable: true, filter: true },
    { headerName: 'Layer', field: 'layer', sortable: true, filter: true },
    { headerName: 'Location', field: 'location', sortable: true, filter: true },
    {
      headerName: 'Message',
      field: 'message',
      flex: 1,
      cellRenderer: (params: any) => {
        // Render message as clickable div to show popup
        const rawValue = params.value || '';
        // Escape for HTML display
        const safeValue = rawValue.replace(/'/g, "&#39;").replace(/"/g, '&quot;');
        // Use encodeURIComponent for safe JS string passing
        const encodedValue = encodeURIComponent(rawValue);
        return `<div style=\"cursor:pointer;white-space:pre-line;overflow:hidden;text-overflow:ellipsis;max-height:40px;\" title=\"Click to view full message\" onclick=\"window.openMessagePopup && window.openMessagePopup(decodeURIComponent('${encodedValue}'))\">${safeValue.length > 200 ? safeValue.substring(0, 200) + '...' : safeValue}</div>`;
      }
    }
  ];

  defaultColDef = {
    resizable: true,
    minWidth: 100
  };

  logData1: LogEntry[] = [];
  logData2: LogEntry[] = [];
  filteredLogData1: LogEntry[] = [];
  filteredLogData2: LogEntry[] = [];

  usageStartDate: string = '';
  usageEndDate: string = '';
  errorStartDate: string = '';
  errorEndDate: string = '';

  constructor(
    private adminService: AdminService,
    private dashboardService: DashboardService,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    // Register the popup handler globally for ag-Grid cellRenderer
    (window as any).openMessagePopup = (msg: string) => {
      this.ngZone.run(() => {
        this.openMessagePopup((msg || '').replace(/\\n/g, '\n'));
      });
    };
    this.adminService.getLogs().subscribe((resp: LogsResponse) => {
      this.logData1 = resp.usageLogs;
      this.logData2 = resp.errorLogs;
      this.filteredLogData1 = [...this.logData1];
      this.filteredLogData2 = [...this.logData2];
    }, (error) => {
      window.location.href = '/';
    });
  }

  showMessagePopup = false;
  popupMessage = '';

  openMessagePopup(msg: string) {
    this.popupMessage = msg;
    this.showMessagePopup = true;
    this.changeDetector.detectChanges();
  }

  closeMessagePopup() {
    this.showMessagePopup = false;
    this.popupMessage = '';
  }

  filterUsageLogs() {
    let filtered = this.filterByTimestamp(this.logData1, this.usageStartDate, this.usageEndDate);
    if (this.usageMessageFilter && this.usageMessageFilter.trim() !== '') {
      const filterText = this.usageMessageFilter.trim().toLowerCase();
      filtered = filtered.filter(entry => (entry.message || '').toLowerCase().includes(filterText));
    }
    this.filteredLogData1 = filtered;
    // If no results, fetch from server
    if (filtered.length === 0 && (this.usageMessageFilter || this.usageStartDate || this.usageEndDate)) {
      this.adminService.getFilteredLogs({
        type: 'usage',
        startDate: this.usageStartDate,
        endDate: this.usageEndDate,
        message: this.usageMessageFilter
      }).subscribe((resp: LogsResponse) => {
        this.logData1 = resp.usageLogs;
        let serverFiltered = this.filterByTimestamp(this.logData1, this.usageStartDate, this.usageEndDate);
        if (this.usageMessageFilter && this.usageMessageFilter.trim() !== '') {
          const filterText = this.usageMessageFilter.trim().toLowerCase();
          serverFiltered = serverFiltered.filter(entry => (entry.message || '').toLowerCase().includes(filterText));
        }
        this.filteredLogData1 = serverFiltered;
      });
    }
  }

  filterErrorLogs() {
    let filtered = this.filterByTimestamp(this.logData2, this.errorStartDate, this.errorEndDate);
    if (this.errorMessageFilter && this.errorMessageFilter.trim() !== '') {
      const filterText = this.errorMessageFilter.trim().toLowerCase();
      filtered = filtered.filter(entry => (entry.message || '').toLowerCase().includes(filterText));
    }
    this.filteredLogData2 = filtered;
    // If no results, fetch from server
    if (filtered.length === 0 && (this.errorMessageFilter || this.errorStartDate || this.errorEndDate)) {
      this.adminService.getFilteredLogs({
        type: 'error',
        startDate: this.errorStartDate,
        endDate: this.errorEndDate,
        message: this.errorMessageFilter
      }).subscribe((resp: LogsResponse) => {
        this.logData2 = resp.errorLogs;
        let serverFiltered = this.filterByTimestamp(this.logData2, this.errorStartDate, this.errorEndDate);
        if (this.errorMessageFilter && this.errorMessageFilter.trim() !== '') {
          const filterText = this.errorMessageFilter.trim().toLowerCase();
          serverFiltered = serverFiltered.filter(entry => (entry.message || '').toLowerCase().includes(filterText));
        }
        this.filteredLogData2 = serverFiltered;
      });
    }
  }

  private filterByTimestamp(data: LogEntry[], start: string, end: string): LogEntry[] {
    if (!start && !end) return data;
    const startTime = start ? new Date(start).getTime() : Number.MIN_SAFE_INTEGER;
    const endTime = end ? new Date(end).getTime() : Number.MAX_SAFE_INTEGER;
    return data.filter(entry => {
      const entryTime = new Date(entry.timestamp).getTime();
      return entryTime >= startTime && entryTime <= endTime;
    });
  }
  selectedTab = 0;

  selectTab(tabIndex: number) {
    this.selectedTab = tabIndex;
    if (tabIndex === 3) {
      this.dashboardService.getDashboardStats().subscribe((stats: any) => {
        this.totalTransmitCount = stats?.result.totalTransmitCount ?? 0;
        this.weeklyTransmitCount = stats?.result.weeklyTransmitCount ?? 0;
        this.claimIdsTransmittedToday = stats?.result.claimIdsTransmittedToday ?? [];
        this.claimIdsTransmittedThisWeek = stats?.result.claimIdsTransmittedThisWeek ?? [];
      });
    }
  }
}
